import { Chip, Popover, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedFilePermissionId } from 'scenes/ProjectGallery/store/selectors/project-gallery-base.selectors';
import { removeTagLinkFromFile } from 'store/mediaTags/thunks/removeTagLinkFromFile.thunk';
import {
  selectIsMediaTagInputActive,
  selectSelectedFilepermissionMediaTags,
} from 'store/mediaTags/selectors/mediaTags.selectors';
import { AddMediaTag } from '../AddMediaTag/AddMediaTag';
import { countFittingTags } from './fitTags';
import { isAiTag, MediaTagChip } from './MediaTagChip';

const GAP = 3;

const useStyles = makeStyles((theme) => ({
  add: {
    flexShrink: 0,
    marginLeft: GAP,
  },
  // A phone has no room for the open field beside the action buttons, so it
  // takes a line of its own below them; the bar in DialogActions wraps, and
  // `order` moves the field past the buttons, and past the reaction row
  // (order 1) that DialogActions puts under them, so the tags keep their line.
  addOpen: {
    [theme.breakpoints.down('xs')]: {
      flexBasis: '100%',
      marginLeft: 0,
      order: 2,
    },
  },
}));

/**
 * The tag strip of the preview dialog's action bar. It stays one line high:
 * as many chips as fit are shown, the rest collapse into a "+N" chip that
 * opens the full list. The action buttons to its right keep their place.
 *
 * The add-tag control sits beside the strip, not inside it, so that when it
 * opens into a text field the strip gives way rather than clipping the field.
 */
export const ImageMediaTags = () => {
  const classes = useStyles();
  const imageTags = useSelector(selectSelectedFilepermissionMediaTags);
  const selectedFileId = useSelector(selectSelectedFilePermissionId);
  const isInputActive = useSelector(selectIsMediaTagInputActive);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  // A person's own tags take the visible slots first; the tagger's follow.
  const orderedTags = useMemo(
    () => [
      ...imageTags.filter((tag) => !isAiTag(tag)),
      ...imageTags.filter(isAiTag),
    ],
    [imageTags]
  );

  const stripRef = useRef(null);
  const measureRef = useRef(null);

  const [visibleCount, setVisibleCount] = useState(orderedTags.length);
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Reads every chip's natural width off the hidden copy of the list, which
  // always holds all of them, so the count never depends on what is shown.
  const measure = useCallback(() => {
    const strip = stripRef.current;
    const row = measureRef.current;
    if (!strip || !row) {
      return;
    }

    const widths = Array.from(row.children).map((child) => child.offsetWidth);
    const overflowWidth = widths.pop() || 0;

    setVisibleCount(
      countFittingTags({
        widths,
        overflowWidth,
        available: strip.clientWidth,
        gap: GAP,
      })
    );
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, orderedTags]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    // Fires for the dialog resizing and for the add-tag control opening into
    // a text field, since both change the width the strip is left with.
    const observer = new ResizeObserver(() => measure());
    observer.observe(stripRef.current);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    setPopoverOpen(false);
  }, [selectedFileId]);

  const hiddenCount = Math.max(orderedTags.length - visibleCount, 0);
  const visibleTags = orderedTags.slice(0, orderedTags.length - hiddenCount);

  const removeTag = (tag) => dispatch(removeTagLinkFromFile(tag.id));

  return (
    <>
      <div
        ref={stripRef}
        style={{
          position: 'relative',
          // A zero basis so the chips never decide where the bar wraps: the
          // strip only ever grows into what the other items leave over.
          flex: '1 1 0px',
          minWidth: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: GAP,
          overflow: 'hidden',
        }}
      >
        {visibleTags.map((tag) => (
          <MediaTagChip
            key={tag.id}
            tag={tag}
            onDelete={() => removeTag(tag)}
          ></MediaTagChip>
        ))}
        {hiddenCount > 0 ? (
          <Tooltip title={t('gallery.show_all_tags')}>
            <Chip
              color="primary"
              clickable
              label={`+${hiddenCount}`}
              onClick={() => setPopoverOpen(true)}
              style={{ opacity: 0.9, fontSize: '14px', flexShrink: 0 }}
            ></Chip>
          </Tooltip>
        ) : null}
        <div
          ref={measureRef}
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
            display: 'flex',
            gap: GAP,
          }}
        >
          {orderedTags.map((tag) => (
            <MediaTagChip
              key={tag.id}
              tag={tag}
              onDelete={() => {}}
            ></MediaTagChip>
          ))}
          {/* The widest "+N" the strip could ever need to make room for. */}
          <Chip
            color="primary"
            label={`+${orderedTags.length}`}
            style={{ fontSize: '14px', flexShrink: 0 }}
          ></Chip>
        </div>
      </div>
      <div
        className={`${classes.add} ${isInputActive ? classes.addOpen : ''}`}
      >
        <AddMediaTag></AddMediaTag>
      </div>
      <Popover
        open={popoverOpen && orderedTags.length > 0}
        anchorEl={stripRef.current}
        onClose={() => setPopoverOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
      >
        <div
          style={{
            padding: 8,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            maxWidth: 400,
          }}
        >
          {orderedTags.map((tag) => (
            <MediaTagChip
              key={tag.id}
              tag={tag}
              onDelete={() => removeTag(tag)}
            ></MediaTagChip>
          ))}
        </div>
      </Popover>
    </>
  );
};
